import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { TooltipProvider } from '@/components/ui/tooltip';
import DynamicPageTitle from './components/common/DynamicPageTitle';

// Pages
import LoginPage from './pages/auth/login/LoginPage';
import RegisterPage from './pages/auth/register/RegisterPage';
import ForgotPasswordPage from './pages/auth/forgot-password/ForgotPasswordPage';
import PanelAccessPage from './pages/auth/panel-access/PanelAccessPage';
import Profile from './pages/auth/profile/Profile';
import IncomePage from './pages/auth/income/IncomePage';
import CustomerDetailsPage from './pages/auth/income/CustomerDetailsPage';
import SettingsPage from './pages/auth/settings/SettingsPage';
import Dashboard from './pages/user/dashboard/Dashboard';
import Tools from './pages/user/tools/Tools';
import SorkariLinks from './pages/user/sorkari-links/SorkariLinks';
import SohozJobList from './pages/user/sohoz-job/SohozJobList';
import { captureReferralFromUrl } from './utils/contestReferral';
import { flushDueResultDeletes } from './utils/privateResults';
import ContestPage from './pages/user/contest/ContestPage';
import JobListing from './pages/user/jobs/JobListing';
import JobDetails from './pages/user/jobs/JobDetails';
import SohozJobSettings from './pages/user/sohoz-job/SohozJobSettings';
import CustomerForm from './pages/user/sohoz-job/CustomerForm';
import CustomerMessages from './pages/user/sohoz-job/CustomerMessages';
import ComingSoon from './pages/user/tools/ComingSoon';
import CompressPdf from './pages/user/tools/pdf/compress/CompressPdf';
import MergePdf from './pages/user/tools/pdf/merge/MergePdf';
import CreatePdf from './pages/user/tools/pdf/create/CreatePdf';
import SplitPdf from './pages/user/tools/pdf/split/SplitPdf';
import PdfToImage from './pages/user/tools/pdf/pdf-to-image/PdfToImage';
import ProtectPdf from './pages/user/tools/pdf/protect/ProtectPdf';
import RotatePdf from './pages/user/tools/pdf/rotate/RotatePdf';
import RemovePagesPdf from './pages/user/tools/pdf/remove-pages/RemovePagesPdf';
import WatermarkPdf from './pages/user/tools/pdf/watermark/WatermarkPdf';
import PermissionsPdf from './pages/user/tools/pdf/permissions/PermissionsPdf';
import ResizePdf from './pages/user/tools/pdf/resize/ResizePdf';
import CropPdf from './pages/user/tools/pdf/crop/CropPdf';
import EditPdf from './pages/user/tools/pdf/edit/EditPdf';
import PdfToExcel from './pages/user/tools/pdf/pdf-to-excel/PdfToExcel';
// PDF to Word is disabled for now (see pdf-to-word/PdfToWord.jsx to re-enable):
// import PdfToWord from './pages/user/tools/pdf/pdf-to-word/PdfToWord';
import RepairPdf from './pages/user/tools/pdf/repair/RepairPdf';
import ExtractImagesPdf from './pages/user/tools/pdf/extract-images/ExtractImagesPdf';
import CompressImage from './pages/user/tools/image/compress/CompressImage';
import DocumentCleanupImage from './pages/user/tools/image/document-cleanup/DocumentCleanupImage';
import PrintReadyMaker from './pages/user/tools/image/print-ready-maker/PrintReadyMaker';
import NidToPdfImage from './pages/user/tools/image/nid-to-pdf/NidToPdfImage';
import PhotoSplitImage from './pages/user/tools/image/photo-split/PhotoSplitImage';
import AiPhotoSplitImage from './pages/user/tools/image/ai-photo-split/AiPhotoSplitImage';
import AiImageToTextTool from './pages/user/tools/text/ai-image-to-text/AiImageToTextTool';
import RemoveBackgroundImage from './pages/user/tools/image/remove-background/RemoveBackgroundImage';
import GovtJobPhotoSignImage from './pages/user/tools/image/govt-job-photo-sign/GovtJobPhotoSignImage';
import PassportToPdfImage from './pages/user/tools/image/passport-to-pdf/PassportToPdfImage';
import ConvertImage from './pages/user/tools/image/convert/ConvertImage';
import QrCodeImage from './pages/user/tools/image/qr-code/QrCodeImage';
import ResizeImage from './pages/user/tools/image/resize/ResizeImage';
import CropImage from './pages/user/tools/image/crop/CropImage';
import ToPdfImage from './pages/user/tools/image/to-pdf/ToPdfImage';
import WatermarkImage from './pages/user/tools/image/watermark/WatermarkImage';
import FlattenImage from './pages/user/tools/image/flatten/FlattenImagePerspectiveTool';
import BarcodeImage from './pages/user/tools/image/barcode/BarcodeImage';
import ColorPickerImage from './pages/user/tools/image/color-picker/ColorPickerImage';
import SignaturePadImage from './pages/user/tools/image/signature-pad/SignaturePadImage';
import SignaturePadMobilePage from './pages/user/tools/image/signature-pad/SignaturePadMobilePage';
import SignatureFromTextImage from './pages/user/tools/image/signature-from-text/SignatureFromTextImage';
import LogoColorChangerImage from './pages/user/tools/image/logo-color-changer/LogoColorChangerImage';
import NoticeGeneratorImage from './pages/user/tools/image/notice-generator/NoticeGeneratorImage';
import CompressGifImage from './pages/user/tools/image/compress-gif/CompressGifImage';
import UpscaleImage from './pages/user/tools/image/upscale/UpscaleImage';
import BijoyUnicodeText from './pages/user/tools/text/bijoy-unicode/BijoyUnicodeText';
import BanglishTypingText from './pages/user/tools/text/banglish-typing/BanglishTypingText';
import NumberToWordsText from './pages/user/tools/text/number-to-words/NumberToWordsText';
import ImageToText from './pages/user/tools/text/image-to-text/ImageToText';
import TextAnalyzerText from './pages/user/tools/text/text-analyzer/TextAnalyzerText';
import DateFormatText from './pages/user/tools/text/date-format/DateFormatText';
import CompressVideo from './pages/user/tools/video/compress/CompressVideo';
import TrimVideo from './pages/user/tools/video/trim/TrimVideo';
import MergeVideo from './pages/user/tools/video/merge/MergeVideo';
import ConvertVideo from './pages/user/tools/video/convert/ConvertVideo';
import WatermarkVideo from './pages/user/tools/video/watermark/WatermarkVideo';
import VolumeVideo from './pages/user/tools/video/volume/VolumeVideo';
import SpeedVideo from './pages/user/tools/video/speed/SpeedVideo';
import ToGifVideo from './pages/user/tools/video/to-gif/ToGifVideo';
import RotateVideo from './pages/user/tools/video/rotate/RotateVideo';
import RemoveAudioVideo from './pages/user/tools/video/remove-audio/RemoveAudioVideo';
import FamilyCardFormGeneral from './pages/user/tools/general/family-card-form/FamilyCardFormGeneral';
import VoterMigrationFormGeneral from './pages/user/tools/general/voter-migration-form/VoterMigrationFormGeneral';
import NidCorrectionFormGeneral from './pages/user/tools/general/nid-information-correction-form/NidCorrectionFormGeneral';
import SpecialInformationFormGeneral from './pages/user/tools/general/special-information-form/SpecialInformationFormGeneral';
import VoterRegistrationFormGeneral from './pages/user/tools/general/voter-registration-form/VoterRegistrationFormGeneral';
import VoterInclusionFormGeneral from './pages/user/tools/general/voter-inclusion-form/VoterInclusionFormGeneral';
import EmbassyBirthFormGeneral from './pages/user/tools/general/embassy-birth-registration-form/EmbassyBirthFormGeneral';
import UdyogFormGeneral from './pages/user/tools/general/udyog-application-form/UdyogFormGeneral';
import BhataTrackingGeneral from './pages/user/tools/general/bhata-tracking/BhataTrackingGeneral';
import FuelCardFormGeneral from './pages/user/tools/general/fuel-card-form/FuelCardFormGeneral';
import AtsCvMakerGeneral from './pages/user/tools/general/ats-cv-maker/AtsCvMakerGeneral';
import AgeCalculatorGeneral from './pages/user/tools/general/age-calculator/AgeCalculatorGeneral';
import BangladeshTravelMapGeneral from './pages/user/tools/general/bangladesh-travel-map/BangladeshTravelMapGeneral';
import EmiCalculatorGeneral from './pages/user/tools/general/emi-calculator/EmiCalculatorGeneral';
import BmiCalculatorGeneral from './pages/user/tools/general/bmi-calculator/BmiCalculatorGeneral';
import TypingPracticeGeneral from './pages/user/tools/general/typing-practice/TypingPracticeGeneral';
import InvoiceDesignerGeneral from './pages/user/tools/general/invoice-designer/InvoiceDesignerGeneral';
import QuestionPaperEditor from './pages/user/question-papers/QuestionPaperEditor';
import EducationBoardResult from './pages/user/tools/result/education-board-result/EducationBoardResult';
import NationalUniversityResult from './pages/user/tools/result/national-university-result/NationalUniversityResult';
import TemplatesBrowser from './pages/user/templates-browser/TemplatesBrowser';
import GeneratedDocuments from './pages/user/generated-documents/GeneratedDocuments';
import OfficeSpaceList from './pages/user/office-spaces/OfficeSpaceList';
import OfficeSpaceSettings from './pages/user/office-space-settings/OfficeSpaceSettings';
import AdminDashboard from './pages/admin/dashboard/AdminDashboard';
import AdminTemplatesPage from './pages/admin/templates/AdminTemplatesPage';
import AdminTagsPage from './pages/admin/tags/AdminTagsPage';
import AdminCategoriesPage from './pages/admin/categories/AdminCategoriesPage';
import DocumentEditorPage from './pages/admin/editor/DocumentEditorPage';
import DocPrintPreviewPage from './pages/admin/editor/DocPrintPreviewPage';
import AdminPrintTemplatesPage from './pages/admin/print-templates/AdminPrintTemplatesPage';
import AdminPrintTemplateCategoriesPage from './pages/admin/print-template-categories/AdminPrintTemplateCategoriesPage';
import AdminPrintFeedbackPage from './pages/admin/print-feedback/AdminPrintFeedbackPage';
import PrintTemplateFormPage from './pages/admin/print-templates/PrintTemplateFormPage';
import PrintMediaBrowser from './pages/user/print-media/PrintMediaBrowser';
import PrintMediaEditor from './pages/user/print-media/PrintMediaEditor';
import GenerateImage from './pages/user/generate-image/GenerateImage';
import BulkPhotoEditDetail from './pages/user/bulk-photo-edit/BulkPhotoEditDetail';
import BulkPhotoEditCreate from './pages/user/bulk-photo-edit/BulkPhotoEditCreate';
import AiDocumentCreate from './pages/user/ai-documents/AiDocumentCreate';
import AiDocumentDetail from './pages/user/ai-documents/AiDocumentDetail';
import GenerateImageManual from './pages/user/generate-image/GenerateImageManual';
import StudioEditor from './pages/user/studio-editor/StudioEditor';
import AiTemplateGallery from './pages/user/ai-templates/AiTemplateGallery';
import AiTemplateGenerate from './pages/user/ai-templates/AiTemplateGenerate';
import AdminAiTemplatesPage from './pages/admin/ai-templates/AdminAiTemplatesPage';
import AdminNegativeFeedbackPage from './pages/admin/ai-templates/AdminNegativeFeedbackPage';
import AiTemplateFormPage from './pages/admin/ai-templates/AiTemplateFormPage';
import AdminAiTemplateCategoriesPage from './pages/admin/ai-template-categories/AdminAiTemplateCategoriesPage';
import AdminJobsPage from './pages/admin/jobs/AdminJobsPage';
import JobFormPage from './pages/admin/jobs/JobFormPage';
import MobileUpload from './pages/mobile-upload/MobileUpload';
import PhotoCollectUpload from './pages/photo-collect/PhotoCollectUpload';
import PaymentSuccess from './pages/payment/PaymentSuccess';
import PaymentFailed from './pages/payment/PaymentFailed';
import OnboardingPage from './pages/auth/onboarding/OnboardingPage';

// Layouts
import PublicLayout from './layouts/PublicLayout';
import PrivateLayout from './layouts/PrivateLayout';
import AdminLayout from './layouts/AdminLayout';
import GlobalSearch from './components/common/GlobalSearch';
import ModuleShortcuts from './components/common/ModuleShortcuts';
import SupportWidget from './components/common/SupportWidget';
import WhatsappFloat from './components/common/WhatsappFloat';
import IncomeExpenseModal from './components/common/IncomeExpenseModal';
import FacebookPromoModal from './components/common/FacebookPromoModal';

function RouteAwareFloats() {
  const location = useLocation();
  const isOnboard = location.pathname === '/onboard';
  if (isOnboard) return null;
  return (
    <>
      <GlobalSearch />
      <ModuleShortcuts />
      <SupportWidget />
      <IncomeExpenseModal />
      <FacebookPromoModal />
    </>
  );
}

function App() {
  // Contest referral link (?ref=CODE): remember it so the registration can credit the referrer.
  useEffect(() => { captureReferralFromUrl(); }, []);
  // A customer's private booth file was downloaded / printed just before this page loaded: finish hiding what was made from it.
  useEffect(() => { void flushDueResultDeletes(); }, []);

  // App-wide image guard: kill the right-click menu ("Open image in new tab",
  // "Save image as…") and HTML5-dragging on every <img>. A casual-copy
  // deterrent only — DevTools can always reach the URL. Images that need
  // native dragging can opt out with an explicit draggable="true" attribute.
  // EXCEPTION: /admin* pages — the system admin needs full native mouse access
  // to images (right-click "Save image as…", drag), so the guard is skipped there.
  useEffect(() => {
    const isAdminRoute = () => window.location.pathname.startsWith('/admin');
    const onContextMenu = (e) => {
      if (isAdminRoute()) return;
      if (e.target?.closest?.('img')) e.preventDefault();
    };
    const onDragStart = (e) => {
      if (isAdminRoute()) return;
      const el = e.target;
      if (el?.tagName === 'IMG' && el.getAttribute('draggable') !== 'true') e.preventDefault();
    };
    document.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('dragstart', onDragStart);
    return () => {
      document.removeEventListener('contextmenu', onContextMenu);
      document.removeEventListener('dragstart', onDragStart);
    };
  }, []);

  return (
    <TooltipProvider delayDuration={300}>
    <BrowserRouter>
      <DynamicPageTitle />
      <WhatsappFloat />
      <RouteAwareFloats />
      <Routes> 

        {/* Public routes */}
        <Route element={<PublicLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/register/operator" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </Route>

        {/* Super admin panel "Sign in as user": receives the token, signs this browser in */}
        <Route path="/panel-access" element={<PanelAccessPage />} />

        {/* User routes */}
        <Route element={<PrivateLayout allowedRoles={['OWNER', 'OPERATOR']} />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/template-sets" element={<TemplatesBrowser />} />
          <Route path="/template-sets/generate/:slug" element={<TemplatesBrowser />} />
          <Route path="/documents" element={<GeneratedDocuments />} />
          <Route path="/documents/regenerate/:setId" element={<GeneratedDocuments />} />
          <Route path="/print-media" element={<PrintMediaBrowser />} />
          <Route path="/tools" element={<Tools />} />
          <Route path="/dorkari-links" element={<SorkariLinks />} />
          <Route path="/contest" element={<ContestPage />} />
          <Route path="/tools/result/education-board-result" element={<EducationBoardResult />} />
          <Route path="/tools/result/national-university-result" element={<NationalUniversityResult />} />
          <Route path="/jobs" element={<JobListing />} />
          <Route path="/jobs/:id" element={<JobDetails />} />
          {/* The poster editor is a window on the jobs page now — old /biggapon links land there. */}
          <Route path="/biggapon" element={<Navigate to="/jobs" replace />} />
          <Route path="/tools/form-auto-fillup" element={<SohozJobList />} />
          <Route path="/tools/form-auto-fillup/settings" element={<SohozJobSettings />} />
          <Route path="/tools/form-auto-fillup/customers/new" element={<CustomerForm />} />
          <Route path="/tools/form-auto-fillup/customers/:id/edit" element={<CustomerForm />} />
          <Route path="/tools/form-auto-fillup/customers/:id/messages" element={<CustomerMessages />} />
          {/* Saved question papers now live in My Files → Questions tab. */}
          <Route path="/questions-create" element={<Navigate to="/documents?tab=questions" replace />} />
          <Route path="/questions-create/create" element={<QuestionPaperEditor />} />
          <Route path="/questions-create/manual" element={<QuestionPaperEditor manual />} />
          <Route path="/questions-create/:id" element={<QuestionPaperEditor />} />
          <Route path="/tools/general/hisabpottro" element={<ComingSoon titleKey="toolsPage.items.hisabpottro.title" descKey="toolsPage.items.hisabpottro.detail" />} />
          <Route path="/tools/pdf/compress" element={<CompressPdf />} />
          <Route path="/tools/pdf/merge" element={<MergePdf />} />
          <Route path="/tools/pdf/create" element={<CreatePdf />} />
          <Route path="/tools/pdf/split" element={<SplitPdf />} />
          <Route path="/tools/pdf/pdf-to-image" element={<PdfToImage />} />
          <Route path="/tools/pdf/protect" element={<ProtectPdf />} />
          <Route path="/tools/pdf/rotate" element={<RotatePdf />} />
          <Route path="/tools/pdf/remove-pages" element={<RemovePagesPdf />} />
          <Route path="/tools/pdf/watermark" element={<WatermarkPdf />} />
          <Route path="/tools/pdf/extract-images" element={<ExtractImagesPdf />} />
          <Route path="/tools/pdf/permissions" element={<PermissionsPdf />} />
          <Route path="/tools/pdf/resize" element={<ResizePdf />} />
          <Route path="/tools/pdf/crop" element={<CropPdf />} />
          <Route path="/tools/pdf/edit" element={<EditPdf />} />
          <Route path="/tools/pdf/pdf-to-excel" element={<PdfToExcel />} />
          {/* PDF to Word is disabled for now (see pdf-to-word/PdfToWord.jsx to re-enable): */}
          {/* <Route path="/tools/pdf/pdf-to-word" element={<PdfToWord />} /> */}
          <Route path="/tools/pdf/repair" element={<RepairPdf />} />
          <Route path="/tools/image/compress" element={<CompressImage />} />
          <Route path="/tools/image/document-cleanup" element={<DocumentCleanupImage />} />
          <Route path="/tools/image/print-ready-maker" element={<PrintReadyMaker />} />
          <Route path="/tools/image/nid-to-pdf" element={<NidToPdfImage />} />
          <Route path="/tools/image/photo-split" element={<PhotoSplitImage />} />
          <Route path="/tools/image/ai-photo-split" element={<AiPhotoSplitImage />} />
          <Route path="/tools/image/remove-background" element={<RemoveBackgroundImage />} />
          <Route path="/tools/image/govt-job-photo-sign" element={<GovtJobPhotoSignImage />} />
          <Route path="/tools/image/passport-to-pdf" element={<PassportToPdfImage />} />
          <Route path="/tools/image/convert" element={<ConvertImage />} />
          <Route path="/tools/image/qr-code" element={<QrCodeImage />} />
          <Route path="/tools/image/resize" element={<ResizeImage />} />
          <Route path="/tools/image/crop" element={<CropImage />} />
          <Route path="/tools/image/to-pdf" element={<ToPdfImage />} />
          <Route path="/tools/image/watermark" element={<WatermarkImage />} />
          <Route path="/tools/image/flatten" element={<FlattenImage />} />
          <Route path="/tools/image/barcode" element={<BarcodeImage />} />
          <Route path="/tools/image/color-picker" element={<ColorPickerImage />} />
          <Route path="/tools/image/signature-pad" element={<SignaturePadImage />} />
          <Route path="/tools/image/signature-from-text" element={<SignatureFromTextImage />} />
          <Route path="/tools/image/logo-color-changer" element={<LogoColorChangerImage />} />
          <Route path="/tools/image/notice-generator" element={<NoticeGeneratorImage />} />
          <Route path="/tools/image/compress-gif" element={<CompressGifImage />} />
          <Route path="/tools/image/upscale" element={<UpscaleImage />} />
          <Route path="/tools/text/bijoy-unicode" element={<BijoyUnicodeText />} />
          <Route path="/tools/text/banglish-typing" element={<BanglishTypingText />} />
          <Route path="/tools/text/number-to-words" element={<NumberToWordsText />} />
          <Route path="/tools/text/image-to-text" element={<ImageToText />} />
          <Route path="/tools/text/ai-image-to-text" element={<AiImageToTextTool />} />
          <Route path="/tools/text/text-analyzer" element={<TextAnalyzerText />} />
          <Route path="/tools/text/date-format" element={<DateFormatText />} />
          <Route path="/tools/video/compress" element={<CompressVideo />} />
          <Route path="/tools/video/trim" element={<TrimVideo />} />
          <Route path="/tools/video/merge" element={<MergeVideo />} />
          <Route path="/tools/video/convert" element={<ConvertVideo />} />
          <Route path="/tools/video/watermark" element={<WatermarkVideo />} />
          <Route path="/tools/video/volume" element={<VolumeVideo />} />
          <Route path="/tools/video/speed" element={<SpeedVideo />} />
          <Route path="/tools/video/to-gif" element={<ToGifVideo />} />
          <Route path="/tools/video/rotate" element={<RotateVideo />} />
          <Route path="/tools/video/remove-audio" element={<RemoveAudioVideo />} />
          <Route path="/tools/general/family-card-form" element={<FamilyCardFormGeneral />} />
          <Route path="/tools/general/voter-migration-form" element={<VoterMigrationFormGeneral />} />
          <Route path="/tools/general/nid-information-correction-form" element={<NidCorrectionFormGeneral />} />
          <Route path="/tools/general/special-information-form" element={<SpecialInformationFormGeneral />} />
          <Route path="/tools/general/voter-registration-form" element={<VoterRegistrationFormGeneral />} />
          <Route path="/tools/general/voter-inclusion-form" element={<VoterInclusionFormGeneral />} />
          <Route path="/tools/general/embassy-birth-registration-form" element={<EmbassyBirthFormGeneral />} />
          <Route path="/tools/general/udyog-application-form" element={<UdyogFormGeneral />} />
          <Route path="/tools/general/bhata-tracking" element={<BhataTrackingGeneral />} />
          <Route path="/tools/general/fuel-card-form" element={<FuelCardFormGeneral />} />
          <Route path="/tools/general/ats-cv-maker" element={<AtsCvMakerGeneral />} />
          <Route path="/tools/general/age-calculator" element={<AgeCalculatorGeneral />} />
          <Route path="/tools/general/bangladesh-travel-map" element={<BangladeshTravelMapGeneral />} />
          <Route path="/tools/general/emi-calculator" element={<EmiCalculatorGeneral />} />
          <Route path="/tools/general/bmi-calculator" element={<BmiCalculatorGeneral />} />
          <Route path="/tools/general/typing-practice" element={<TypingPracticeGeneral />} />
          <Route path="/tools/general/invoice-designer" element={<InvoiceDesignerGeneral />} />
          <Route path="/print-media/editor/:templateId" element={<PrintMediaEditor />} />
          <Route path="/officespaces" element={<OfficeSpaceList />} />
          <Route path="/officespaces/:id/settings" element={<OfficeSpaceSettings />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/onboard" element={<OnboardingPage />} />
          <Route path="/income" element={<IncomePage />} />
          <Route path="/income/customers/:id" element={<CustomerDetailsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/generate/:templateSetId" element={<Dashboard />} />
          <Route path="/generate-image" element={<GenerateImage />} />
          <Route path="/generate-image-manual" element={<GenerateImageManual />} />
          <Route path="/studio-editor" element={<StudioEditor />} />
          {/* Bulk photo edit opens straight into the editor; the project list now
              lives under My Files → Bulk Photo Edit (GeneratedDocuments). */}
          <Route path="/bulk-photo-edit" element={<BulkPhotoEditCreate />} />
          <Route path="/bulk-photo-edit/new" element={<BulkPhotoEditCreate />} />
          <Route path="/bulk-photo-edit/:id" element={<BulkPhotoEditDetail />} />
          <Route path="/ai-documents" element={<AiDocumentCreate />} />
          <Route path="/ai-documents/new" element={<AiDocumentCreate />} />
          <Route path="/ai-documents/:id" element={<AiDocumentDetail />} />
          <Route path="/ai-templates" element={<AiTemplateGallery />} />
          <Route path="/ai-templates/:slug" element={<AiTemplateGenerate />} />
          {/* Back-compat: old /worldcup-jersey paths still resolve */}
          <Route path="/worldcup-jersey" element={<Navigate to="/ai-templates" replace />} />
          <Route path="/worldcup-jersey/:slug" element={<AiTemplateGenerate />} />
        </Route>

        {/* Admin routes */}
        <Route element={<AdminLayout />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/template-sets" element={<AdminTemplatesPage />} />
          <Route path="/admin/template-sets/new" element={<AdminTemplatesPage />} />
          <Route path="/admin/template-sets/:id/edit" element={<AdminTemplatesPage />} />
          <Route path="/admin/print-templates" element={<AdminPrintTemplatesPage />} />
          <Route path="/admin/print-templates/new" element={<PrintTemplateFormPage />} />
          <Route path="/admin/print-templates/:id/edit" element={<PrintTemplateFormPage />} />
          <Route path="/admin/print-template-categories" element={<AdminPrintTemplateCategoriesPage />} />
          <Route path="/admin/print-feedback" element={<AdminPrintFeedbackPage />} />
          <Route path="/admin/tags" element={<AdminTagsPage />} />
          <Route path="/admin/categories" element={<AdminCategoriesPage />} />
          <Route path="/admin/generate-image" element={<GenerateImage />} />
          <Route path="/admin/generate-image-manual" element={<GenerateImageManual />} />
          <Route path="/admin/ai-templates" element={<AdminAiTemplatesPage />} />
          <Route path="/admin/ai-templates/negative-feedback" element={<AdminNegativeFeedbackPage />} />
          <Route path="/admin/ai-templates/new" element={<AiTemplateFormPage />} />
          <Route path="/admin/ai-templates/:id/edit" element={<AiTemplateFormPage />} />
          <Route path="/admin/ai-template-categories" element={<AdminAiTemplateCategoriesPage />} />
          <Route path="/admin/jobs" element={<AdminJobsPage />} />
          <Route path="/admin/jobs/new" element={<JobFormPage />} />
          <Route path="/admin/jobs/:id/edit" element={<JobFormPage />} />
          <Route path="/admin/profile" element={<Profile />} />
          <Route path="/admin/settings" element={<SettingsPage />} />
        </Route>

        {/* Mobile upload route (public, no layout, no auth) */}
        <Route path="/mobile-upload/:token" element={<MobileUpload />} />

        {/* Permanent QR photo-collection upload (public, no layout, no auth) */}
        <Route path="/u/:slug" element={<PhotoCollectUpload />} />

        {/* Signature pad mobile relay (public, no auth) */}
        <Route path="/signature-mobile/:sessionSlug" element={<SignaturePadMobilePage />} />

        {/* Payment result pages — gateway redirects land here, no layout */}
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/failed" element={<PaymentFailed />} />

        {/* Admin editor routes (fullscreen, no layout) */}
        <Route path="/admin/doc-editor/:docId" element={<DocumentEditorPage />} />
        <Route path="/admin/lexical/:id/:mode" element={<DocumentEditorPage />} />
        <Route path="/admin/lexical-editor/:templateSetId/:docId" element={<DocumentEditorPage />} />
        <Route path="/admin/doc-print-preview/:templateSetId/:docId" element={<DocPrintPreviewPage />} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
    </TooltipProvider>
  );
}

export default App;
